import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";

// Import all your templates
import PremiumModernTemplate from "../Package/PackagePdfTemplates/PremiumModernTemplate";
import SimpleEleganceTemplate from "../Package/PackagePdfTemplates/SimpleEleganceTemplate";
import ModernCorporateTemplate from "../Package/PackagePdfTemplates/ModernCorporateTemplate";
import LuxuryMagazineTemplate from "../Package/PackagePdfTemplates/LuxuryMagazineTemplate";
import VibrantAdventureTemplate from "../Package/PackagePdfTemplates/VibrantAdventureTemplate";
import ClassicEleganceTemplate from "../Package/PackagePdfTemplates/ClassicEleganceTemplate";
import GeoExplorerTemplate from "../Package/PackagePdfTemplates/GeoExplorerTemplate";
import ArtisanJournalTemplate from "../Package/PackagePdfTemplates/ArtisanJournalTemplate";
import SimpleTemplate from "../Package/PackagePdfTemplates/SimpleTemplate";

// --- STYLES FOR THE DOCUMENT PREVIEW PAGE ---
const styles = {
  pageBackground: {
    backgroundColor: '#e0e0e0',
    padding: '2rem 0',
  },
  pagesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem', 
  },
  a4Page: {
    width: '210mm', 
    minHeight: '297mm', 
    backgroundColor: 'white',
    boxShadow: '0 0 15px rgba(0,0,0,0.2)',
    overflow: 'hidden', 
    position: 'relative',
  },
  contentWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 'auto',
  },
};

// --- REUSABLE PAGINATION COMPONENT ---
const FullPagePaginatedView = ({ children }) => {
  const [totalPages, setTotalPages] = useState(1);
  const contentRef = useRef(null);

  const A4_HEIGHT_MM = 297;

  const mmToPx = (mm) => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.height = `${mm}mm`;
    document.body.appendChild(div);
    const px = div.offsetHeight;
    document.body.removeChild(div);
    return px;
  };

  useLayoutEffect(() => {
    document.body.style.backgroundColor = styles.pageBackground.backgroundColor;

    if (contentRef.current) {
      const pageHeightPx = mmToPx(A4_HEIGHT_MM);
      const observer = new ResizeObserver(entries => {
        const contentHeight = entries[0].target.scrollHeight;
        const pages = Math.ceil(contentHeight / pageHeightPx);
        setTotalPages(pages > 0 ? pages : 1);
      });
      observer.observe(contentRef.current);
      return () => {
        observer.disconnect();
        document.body.style.backgroundColor = ''; 
      };
    }
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [children]);
  
  const pageHeightPx = mmToPx(A4_HEIGHT_MM);

  return (
    <>
      <div style={{ position: 'absolute', opacity: 0, zIndex: -10, width: '210mm' }}>
        <div ref={contentRef}>{children}</div>
      </div>
      <div style={styles.pagesContainer}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <div key={index} style={styles.a4Page}>
            <div style={{
                ...styles.contentWrapper,
                transform: `translateY(-${index * pageHeightPx}px)`,
            }}>
              {children}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const HolidayTemplatePreviewPage = () => {
  const { packageId, templateId } = useParams(); // packageId here is actually holidayId
  const { user } = useAuth();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const templates = [
    { id: "simple-elegance", name: "Simple Elegance", component: SimpleEleganceTemplate },
    { id: "classic-elegance", name: "Classic Elegance", component: ClassicEleganceTemplate },
    { id: "modern-corporate", name: "Modern Corporate", component: ModernCorporateTemplate },
    { id: "vibrant-adventure", name: "Vibrant Adventure", component: VibrantAdventureTemplate },
    { id: "geo-explorer", name: "Geo Explorer", component: GeoExplorerTemplate },
    { id: "premium-modern", name: "Premium Modern", component: PremiumModernTemplate },
    { id: "luxury-magazine", name: "Luxury Magazine", component: LuxuryMagazineTemplate },
    { id: "artisan-journal", name: "Artisan Journal", component: ArtisanJournalTemplate },
    { id: "simple-template", name: "Simple Template", component: SimpleTemplate },
  ];

  // --- DATA FETCHING WITH ADAPTER ---
  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        const res = await apiService.get(`${config.GETHOLIDAYBYID}/${packageId}`);
        const holiday = res.data || res;
        
        // --- ADAPTER LOGIC START (Same as HolidayTemplates.js) ---
        const parseAndNormalize = (jsonString) => {
            if (!jsonString) return [];
            try {
                const parsed = JSON.parse(jsonString);
                return parsed.map(item => {
                    const newItem = {};
                    Object.keys(item).forEach(key => {
                        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
                        newItem[newKey] = item[key];
                    });
                    return newItem;
                });
            } catch (e) {
                console.error("Parse Error", e);
                return [];
            }
        };

        const transformedData = {
            package: {
                packageName: holiday.holidayName,
                description: holiday.description,
                duration: holiday.duration,
                destinationsCover: holiday.destinationsCover,
                price: holiday.salePrice, 
                currency: holiday.currency || "INR",
                imageSrc: holiday.imageSrc,
            },
            itineraries: parseAndNormalize(holiday.holidayItinerary),
            flightBookings: parseAndNormalize(holiday.holidayFlight),
            hotelBookings: parseAndNormalize(holiday.holidayHotel),
            sightSeeings: parseAndNormalize(holiday.holidaySightSeeing),
            transfers: parseAndNormalize(holiday.holidayTransfer),
            includes: parseAndNormalize(holiday.holidayInclusions || holiday.holidayInclude),
            excludes: parseAndNormalize(holiday.holidayExclusions),
            termsandConditions: parseAndNormalize(holiday.holidayTerms),
        };
        // --- ADAPTER LOGIC END ---

        setPackageData(transformedData);
      } catch (error) {
        console.error("Error fetching holiday details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (packageId) {
      fetchPackageData();
    }
  }, [packageId]);

  const renderTemplate = () => {
    if (!packageData || !templateId) return null;
    
    const template = templates.find((t) => t.id === templateId);
    if (!template) return <div>Template not found.</div>;
    
    const TemplateComponent = template.component;
    return <TemplateComponent packageData={packageData} user={user} />;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: styles.pageBackground.backgroundColor }}>
        <Spinner animation="border" /> <span className="ms-2">Loading Template Preview...</span>
      </div>
    );
  }

  return (
    <FullPagePaginatedView>
      {renderTemplate()}
    </FullPagePaginatedView>
  );
};

export default HolidayTemplatePreviewPage;